// Названия из данных на языке интерфейса.
//
// Бэкенд при locale=en «переводит» названия сам (cdo-backend,
// app/services/localization.py: translate_text / translate_territory_name /
// transliterate) — по глоссарию из нескольких десятков слов, а всё остальное
// транслитерирует: «VVP na dushu population», «Tysyacha rubley»,
// «Aleyskiy munitsipalnyy rayon». Поэтому данные и каталоги запрашиваются
// всегда на русском (api.ts), а здесь подставляются настоящие английские
// названия, где они есть в самих данных: name_en показателей и стран World
// Bank, name_en территорий каталога, словарь единиц, названий показателей
// общих дашбордов и частых значений разрезов (locales/ru-en.json, раздел
// data). Остальное остаётся русским.

import type { DataQueryResponse, Indicator, IndicatorDimension, Locale, Territory } from './types';
import { dataTranslation, trData } from './ui-i18n';

const territoryNamesEn = new Map<string, string>();

function englishName(metadata: Record<string, unknown> | undefined, key = 'name_en'): string | null {
  const value = metadata?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Запоминает английские названия территорий: строки данных их не несут. */
export function rememberTerritoryNames(territories: Territory[]): void {
  territories.forEach((territory) => {
    const name = englishName(territory.metadata);
    if (name) territoryNamesEn.set(territory.id, name);
  });
}

export function localizeTerritories(territories: Territory[], locale: Locale): Territory[] {
  rememberTerritoryNames(territories);
  if (locale !== 'en') return territories;
  return territories.map((territory) => {
    const name = englishName(territory.metadata);
    return name ? { ...territory, name, metadata: { ...territory.metadata, name_ru: territory.name } } : territory;
  });
}

/** Название территории по-русски и по-английски, при любом языке интерфейса. */
export function territoryNames(territory: Territory): { ru: string; en: string } {
  const ru = englishName(territory.metadata, 'name_ru') ?? territory.name;
  return { ru, en: englishName(territory.metadata) ?? territoryNamesEn.get(territory.id) ?? ru };
}

/** Название показателя (краткое, если есть) по-русски и по-английски, при любом языке интерфейса. */
export function indicatorNames(indicator: Indicator): { ru: string; en: string } {
  const metadata = indicator.source_metadata;
  const original = englishName(metadata, 'name_ru') ?? indicator.name;
  const ru = englishName(metadata, 'short_name_ru') ?? (englishName(metadata, 'name_ru') ? original : (indicator.short_name || indicator.name));
  return { ru, en: englishName(metadata) ?? dataTranslation(ru) ?? dataTranslation(original) ?? ru };
}

export function localizeDimensions(dimensions: IndicatorDimension[], locale: Locale): IndicatorDimension[] {
  if (locale !== 'en') return dimensions;
  return dimensions.map((dimension) => ({
    ...dimension,
    name: trData(dimension.name),
    values: dimension.values.map((value) => ({ ...value, label: trData(value.label) })),
  }));
}

export function localizeIndicator<T extends Indicator>(indicator: T, locale: Locale): T {
  if (locale !== 'en') return indicator;
  const metadata = indicator.source_metadata;
  const name = englishName(metadata);
  const note = englishName(metadata, 'source_note_en');
  // name_en есть только у World Bank; БДМО, ЕМИСС и населённые пункты —
  // по словарю (locales/ru-en.json, раздел data), иначе по-русски.
  return {
    ...indicator,
    name: name ?? trData(indicator.name),
    short_name: name ?? (indicator.short_name ? trData(indicator.short_name) : indicator.short_name),
    unit: trData(indicator.unit),
    description: note ?? indicator.description,
    dimensions: localizeDimensions(indicator.dimensions ?? [], locale),
    // Русские названия — для заголовков виджетов на обоих языках (indicatorNames).
    source_metadata: {
      ...metadata,
      name_ru: englishName(metadata, 'name_ru') ?? indicator.name,
      short_name_ru: indicator.short_name ?? null,
    },
  };
}

/** «Барнаул · Мужчины» → «City of Barnaul · Men»: территория — английским названием, остальное — словарём. */
function localizedSeriesName(row: DataQueryResponse['rows'][number], territoryName: string): string | null | undefined {
  if (!row.series_name) return row.series_name;
  const whole = trData(row.series_name);
  if (whole !== row.series_name) return whole;
  const parts = row.series_name.split(' · ');
  return parts
    .map((part, index) => (index === 0 && part === row.territory_name ? territoryName : trData(part)))
    .join(' · ');
}

export function localizeDataResponse(response: DataQueryResponse, locale: Locale): DataQueryResponse {
  // Английские имена из строк — и для подсказок карт (featureName), на любом языке.
  response.rows.forEach((row) => {
    const english = row.territory_name_en?.trim();
    if (english) territoryNamesEn.set(row.territory_id, english);
  });
  if (locale !== 'en') return response;
  return {
    ...response,
    indicator: localizeIndicator(response.indicator, locale),
    rows: response.rows.map((row) => {
      const name = row.territory_name_en?.trim() || territoryNamesEn.get(row.territory_id) || row.territory_name;
      return { ...row, territory_name: name, series_name: localizedSeriesName(row, name) };
    }),
  };
}

/** Название объекта геослоя (карты): name_en, если есть. */
export function featureName(properties: Record<string, unknown>, locale: Locale): string | null {
  const english = locale === 'en' ? englishName(properties) : null;
  const territoryId = typeof properties.territory_id === 'string' ? properties.territory_id : '';
  const known = locale === 'en' && territoryId ? territoryNamesEn.get(territoryId) : undefined;
  const name = english ?? known ?? properties.name ?? properties.territory_name;
  return typeof name === 'string' && name ? name : null;
}
